import { getRouteApi } from '@tanstack/react-router'
import { IconSize } from 'ui/components'
import { DateTime } from 'ui/components/DateTime'
import { Hr } from 'ui/components/Hr'
import { Info, InfoSet } from 'ui/components/Info'
import { FrequencyLabel } from 'ui/components/Labels'
import { Link } from 'ui/components/Link'
import { PageSection, PageSectionFace } from 'ui/components/Page'
import { TextMask } from 'ui/components/TextMask'
import { Col } from 'ui/themes/globalStyles'
import { DocumentActions } from '~bank-documents/components/DocumentActions'
import { DocumentPeriodStatus } from '~bank-documents/components/DocumentPeriodStatus'
import { RelatedCompany } from '~bank-documents/components/RelatedCompany'
import { DocumentDetailsPage } from '~bank-documents/types/documentDetails'

import { s } from './styles'

export const DocumentDetailsInformation = () => {
  const routeApi = getRouteApi('/_app/documents/$documentId')
  const documentId = routeApi.useParams({ select: (s) => s.documentId })
  const data = routeApi.useLoaderData() as DocumentDetailsPage

  const {
    showDueDateType,
    name,
    expirationDate,
    frequency,
    internal,
    subjectType,
    showRelatedCompanies,
    showBankAccountId,
    showCompanyId,
    showContactId,
    showLicenseId,
    showCompanyName,
    showContactName,
    showLicenseName,
    showBankAccountNumber,
    showStatus,
    showStartDateType,
    showNotes,
    createdAt,
    lastModifiedAt,
    expirationDelayDays,
    showDisablePeriodsCreation,
    showNotesLabel
  } = data

  return (
    <PageSection
      face={PageSectionFace.SECONDARY}
      title="Document Information"
      actions={
        <DocumentActions
          editButtonParams={{
            to: '/documents/$documentId/edit',
            params: { documentId: Number(documentId) },
            search: { returnUrl: `/documents/${documentId}` }
          }}
        />
      }
    >
      <Hr />

      <InfoSet legend="Client Document Details">
        <s.EnlargedRow>
          <Col>
            <Info label="Document Name">{name}</Info>
          </Col>

          <Col>
            <Info label="Internal">{internal ? 'Yes' : 'No'}</Info>
          </Col>
        </s.EnlargedRow>

        <s.EnlargedRow>
          <Col>
            <Info label="Subject Type">{subjectType}</Info>
          </Col>

          <Col>
            {showBankAccountId && (
              <Info label="Bank Account">
                <Link to="/bank-accounts/$bankAccountId" params={{ bankAccountId: showBankAccountId.toString() }}>
                  <TextMask isBankAccount text={showBankAccountNumber} iconSize={IconSize.XS} />
                </Link>
              </Info>
            )}

            {showLicenseId && (
              <Info label="License">
                <Link to="/licenses/$licenseId" params={{ licenseId: showLicenseId.toString() }}>
                  {showLicenseName}
                </Link>
              </Info>
            )}
            {showContactId && (
              <Info label="Contact">
                <Link to="/contacts/$contactId" params={{ contactId: showContactId.toString() }}>
                  {showContactName}
                </Link>
              </Info>
            )}
          </Col>
        </s.EnlargedRow>

        <s.EnlargedRow>
          <Col>
            <Info label="Frequency">
              <FrequencyLabel name={frequency} dataQa="frequency label" />
            </Info>
          </Col>

          <Col>
            {showStatus && (
              <Info label="Status">
                <DocumentPeriodStatus name={showStatus} />
              </Info>
            )}
            {!!showDueDateType && <Info label="Due Date Type">{showDueDateType}</Info>}
          </Col>
        </s.EnlargedRow>

        <s.EnlargedRow>
          <Col>
            <RelatedCompany
              asInfoBlock={true}
              relatedCompanies={showRelatedCompanies || [{ id: showCompanyId, name: showCompanyName }]}
            />
          </Col>

          <Col>{showStartDateType && <Info label="Start Date Type">{showStartDateType}</Info>}</Col>
        </s.EnlargedRow>

        <s.EnlargedRow>
          <Col>
            <Info label="Expiration Date">{expirationDate}</Info>
          </Col>
          <Col>
            <Info label="Expiration Delay Days">{expirationDelayDays}</Info>
          </Col>
        </s.EnlargedRow>

        <s.EnlargedRow>
          <Col>
            <Info label="Created At">
              <DateTime date={createdAt} withTime />
            </Info>
          </Col>
          <Col>
            <Info label="Last Modified At">
              <DateTime date={lastModifiedAt} withTime />
            </Info>
          </Col>
        </s.EnlargedRow>

        {showDisablePeriodsCreation !== undefined && (
          <s.EnlargedRow>
            <Col>
              <Info label="Disable Periods Creation">{showDisablePeriodsCreation ? 'Yes' : 'No'}</Info>
            </Col>
          </s.EnlargedRow>
        )}

        {showNotes !== undefined && <Info label={showNotesLabel}>{showNotes}</Info>}
      </InfoSet>
      <Hr />
    </PageSection>
  )
}
